import React, { useEffect, useState, memo, useRef } from "react";
import {
  TextField, Button, Grid, Box, Switch, FormControlLabel, FormControl, Dialog, DialogActions, DialogContent, DialogTitle, Checkbox
} from "@mui/material";
import { useAuth, useSnackbar, useLoading, useNavigation } from "../../../Context/Contexts";
import CenteredCard from "../../../components/Card/CenteredCardLayout";
import { SalespersonViewModel } from "../../../Types/ExportTypes";
import DefaultActions from "../../../components/Card/DefaultActions";
import { LanguageAutoComplete } from "../../../components/AutoComplete/AutoCompletes";
import { useTranslation } from "react-i18next";
import { ToolbarWithSaveHandle } from "../../../components/Toolbar/ToolbarWithSave";

const SalespersonEditPage: React.FC<{ params?: { [key: string]: any } }> = ({ params }) => {
  const { code = "" } = params || {};
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const { showLoading, hideLoading } = useLoading();
  const { authService } = useAuth();
  const { showSnackbar } = useSnackbar();
  const { goBack, navigate } = useNavigation();
  const [viewModel, setViewModel] = useState<(SalespersonViewModel & { hasChanges?: boolean; originalValues?: Partial<SalespersonViewModel>; }) | null>(null);
  const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const toolbarRef = useRef<ToolbarWithSaveHandle>(null);
  const showHasChangesError = () => {
    toolbarRef.current?.showHasChangesError();
  };
  useEffect(() => {
    if (isLoading) showLoading();
    else hideLoading();
  }, [isLoading, showLoading, hideLoading]);

  useEffect(() => {
    if (code) {
      setIsLoading(true);
      authService
        .get<SalespersonViewModel>(`/settingsApi/GetSalesperson?code=${code}`)
        .then((response) =>
          setViewModel(response ? { ...response, originalValues: { ...response }, hasChanges: false } : null)
        )
        .catch((error: any) => {
          showSnackbar(`${t("common.failedToFetchEntries")}: ${error.response?.data ?? error.message}`, "error");
        })
        .finally(() => setIsLoading(false));
    } else {
      // Default empty location object for creating new entries
      const defaultSalesperson: SalespersonViewModel & { originalValues: Partial<SalespersonViewModel>; hasChanges: boolean } = {
        code: "",
        name: "",
        createNew: true,
        originalValues: {},
        hasChanges: false,
      };
      defaultSalesperson.originalValues = { ...defaultSalesperson };
      setViewModel(defaultSalesperson);
    }
  }, [code, authService]);

  const handleSave = async () => {
    if (!viewModel) return;
    if (!validateViewModel()) return; // Prevent save if validation fails
    var newItem = viewModel.createNew ?? false;

    setIsLoading(true);
    try {
      const response = await authService.post<SalespersonViewModel>("/settingsApi/CreateOrUpdateSalesPerson", viewModel);
      setViewModel(response ? { ...response, originalValues: { ...response }, hasChanges: false } : null);
      showSnackbar(t("dialog.savedSuccessfully"), "success");

      if (newItem) {
        navigate({ path: "/settings/salesperson/edit", params: { code: response.code } });
      }
    } catch {
      showSnackbar(t("dialog.saveErrorGeneric"), "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field: keyof SalespersonViewModel, value: any) => {
    setViewModel((prev) => (prev ? { ...prev, [field]: value, hasChanges: true } : null));
  };

  const handleCancelChanges = () => {
    setViewModel((prev) => {
      if (!prev || !prev.originalValues) return prev;
      const original = prev.originalValues as SalespersonViewModel;
      return { ...original, originalValues: { ...original }, hasChanges: false };
    });
  };


  const handleDelete = async () => {

    const isConfirmed = window.confirm(t("dialog.deleteEntityConfirm"));
    if (!isConfirmed) return;
    var newItem = viewModel?.createNew ?? false;
    if (newItem) {
      navigate({ path: "/settings/salesperson/list" });
      return;
    }

    setIsLoading(true);
    try {
      await authService.post(`/settingsApi/DeleteSalesperson`, viewModel);
      showSnackbar(t("dialog.deletedSuccessfully"), "success");;

      navigate({ path: "/settings/salesperson/list" });
    } catch (error: any) {
      showSnackbar(`${t("dialog.deleteFailed")} : ${error.response?.data ?? error.message}`, "error");

    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = async (newCode?: string) => {

    var newItem = viewModel?.createNew ?? false;
    if (newItem) {
      showSnackbar(t("dialog.salespersonCopyPreliminary"), "warning");
      return;
    }

    if (newCode && newCode === viewModel?.code) {
      showSnackbar(t("dialog.salespersonCodeSame"), "warning");
      return;
    }

    // If newCode is provided, update viewModel and continue
    if (newCode) {
      setViewModel(prev => ({ ...prev, newCode }));
    }


    // If newCode isn't set, open the modal and return
    if (!viewModel?.newCode && !newCode) {
      setIsCopyModalOpen(true);
      return;
    }
    setIsCopyModalOpen(false);
    setIsLoading(true);
    try {
      var response = await authService.post<SalespersonViewModel>(`/settingsApi/CopySalesperson`, { ...viewModel, newCode });
      setViewModel(response);
      showSnackbar(t("dialog.salespersonCopied"), "success");

      navigate({ path: "/settings/salesperson/edit", params: { code: response.code } });

    } catch (error: any) {

      showSnackbar(`${t("dialog.salespersonCopyFailed")} : ${error.response?.data ?? error.message}`, "error");

      setViewModel(prev => ({ ...prev, newCode: null }));

    } finally {
      setIsLoading(false);
    }
  };


  type CodeInputModalProps = {
    open: boolean;
    onClose: () => void;
    onConfirm: (newCode: string) => void;
    ViewModel?: SalespersonViewModel | null
  };
  const CodeInputModal = memo(({ open, onClose, onConfirm, ViewModel }: CodeInputModalProps) => {
    const [tempCode, setTempCode] = useState("");
    return (
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>{t("dialog.enterNewCode")}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t("common.newCode")}
            fullWidth
            variant="outlined"
            value={tempCode}
            onChange={(e) => setTempCode(e.target.value)} // Only updates local state
            error={ViewModel?.code === tempCode}
            helperText={ViewModel?.code === tempCode ? t("help.salespersonCodeSame") : ""}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t("common.cancel")}</Button>
          <Button
            onClick={() => {
              if (!tempCode.trim()) {
                showSnackbar(t("dialog.codeEmpty"), "warning");
                return;
              }
              onConfirm(tempCode?.trim()); // Pass only on confirm
            }}
            color="primary"
          >
            {t("common.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    );
  });

  const validateViewModel = () => {
    if (!viewModel) {
      showSnackbar(t("dialog.dataMissing"), "error");
      return false;
    }

    if (!viewModel.code || viewModel.code.trim() === "") {
      showSnackbar(t("dialog.codeEmpty"), "warning");
      return false;
    }

    if (!viewModel.name || viewModel.name.trim() === "") {
      showSnackbar(t("dialog.nameRequired"), "warning");
      return false;
    }


    return true;
  };
  return (
    <>

      <CenteredCard
        ref={toolbarRef}
        title={t("common.edit") + " " + t("common.salesperson")}
        isLoading={isLoading}
        handleSave={handleSave}
        handleCancel={handleCancelChanges}
        hasChanges={viewModel?.hasChanges ?? false}
      >
        <Box sx={{
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
          maxWidth: "fit-content",
          mb: 2
        }}>
          <DefaultActions
            onSave={handleSave}
            onDelete={handleDelete}
            onCopy={handleCopy}
          />
        </Box>
        <CodeInputModal open={isCopyModalOpen} onClose={() => setIsCopyModalOpen(false)} onConfirm={handleCopy} ViewModel={viewModel} />
        {viewModel && (
          <>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label={t("common.code")}
                  value={viewModel.code || ""}
                  onChange={(e) => handleChange("code", e.target.value)}
                  fullWidth
                  margin="normal"
                  disabled={viewModel.createNew !== true}
                  error={!viewModel.code || viewModel.code.trim() === ""}
                  helperText={!viewModel.code || viewModel.code.trim() === "" ? t("help.codeRequired") : ""}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Name"
                  value={viewModel.name || ""}
                  onChange={(e) => handleChange("name", e.target.value)}
                  fullWidth
                  margin="normal"
                  error={!viewModel.name || viewModel.name.trim() === ""}
                  helperText={!viewModel.name || viewModel.name.trim() === "" ? t("help.nameRequired") : ""}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Pin"
                  type="number"
                  value={viewModel.termpin || ""}
                  onChange={(e) => handleChange("termpin", e.target.value)}
                  fullWidth
                  margin="normal"
                  helperText={t("help.posPincode")}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth margin="normal">
                  <LanguageAutoComplete
                    key={viewModel.xLanguageCode || null}
                    valueId={viewModel.xLanguageCode || null}
                    onChange={(selected) => handleChange("xLanguageCode", selected?.value || "")}
                    infoText={t("help.posLanguage")}

                  />
                </FormControl>
              </Grid>
              {/*<Grid item xs={6}>*/}
              {/*  <FormControlLabel*/}
              {/*    control={<Checkbox checked={!!viewModel.administrator} onChange={(e) => handleChange("administrator", e.target.checked)} />}*/}
              {/*    label={t("common.administrator")}*/}
              {/*  />*/}
              {/*</Grid>*/}
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Checkbox checked={!!viewModel.blocked} onChange={(e) => handleChange("blocked", e.target.checked)} />}
                  label={t("common.blocked")}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch checked={showSettings} onChange={(e) => setShowSettings(e.target.checked)} />}
                  label={t("common.showPermissions")}
                />
              </Grid>


              {showSettings && (
                <>
                  <Box
                    sx={{
                      backgroundColor: "secondary.main",
                      padding: 2,
                      borderRadius: 2,
                      width: "100%"
                    }}
                  >


                    <Grid item xs={12}>
                      <FormControlLabel
                        control={<Checkbox checked={!!viewModel.allowDisc} onChange={(e) => handleChange("allowDisc", e.target.checked)} />}
                        label={t("common.allowDiscount")}
                      />

                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={<Checkbox checked={!!viewModel.allowPriceChange} onChange={(e) => handleChange("allowPriceChange", e.target.checked)} />}
                        label={t("common.allowPriceChange")}
                      />


                    </Grid>

                    <Grid item xs={12}>
                      <FormControlLabel
                        control={<Checkbox checked={!!viewModel.allowCashOut} onChange={(e) => handleChange("allowCashOut", e.target.checked)} />}
                        label={t("common.allowCashWithdrawal")}
                      />

                    </Grid>
                  </Box>
                </>
              )}
            </Grid>
          </>
        )}
        <Box sx={{ marginTop: 2, textAlign: "center" }}>
          <Button variant="contained" onClick={handleSave} disabled={isLoading} sx={{ marginRight: 2 }}>
            {t("common.save")}
          </Button>
          <Button variant="outlined" onClick={goBack}>
            {t("common.back")}
          </Button>
        </Box>
      </CenteredCard>


    </>
  );
};

export default SalespersonEditPage;
